import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '../../components/ui/Button';
import { Icon } from '../../components/ui/Icon';
import { Input, Select } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import type { Opcion } from '../../../data/repositories/RepositorioCatalogos';
import {
  repositorioUsuarios,
  type UsuarioAdmin,
} from '../../../data/repositories/RepositorioUsuarios';
import { Rol, ROLES_CANONICOS, type RolCanonico } from '../../../domain/value-objects/Rol';
import estilos from '../../components/layout/PaginaCrud.module.css';

/** Datos que devuelve el formulario, ya validados. */
export interface ResultadoFormularioUsuario {
  email: string;
  claveInicial: string;
  nombre: string;
  telefono: string | null;
  rol: RolCanonico;
  clienteId: number | null;
}

interface FormularioUsuarioProps {
  abierto: boolean;
  /** `null` para crear una cuenta nueva. */
  usuario: UsuarioAdmin | null;
  clientes: readonly Opcion[];
  guardando: boolean;
  onCerrar: () => void;
  onGuardar: (datos: ResultadoFormularioUsuario) => void;
}

const CAMPOS_VACIOS = {
  email: '',
  clave: '',
  nombre: '',
  telefono: '',
  rol: 'cliente_final' as RolCanonico,
  clienteId: '',
};

const LARGO_MINIMO_CLAVE = 8;

/** Qué puede hacer cada rol, en una línea, para elegir con criterio. */
const QUE_PUEDE_HACER: Readonly<Record<RolCanonico, string>> = {
  superadmin: 'Acceso total a todos los clientes y a la configuración del sistema.',
  admin_cliente: 'Gestiona su cliente: equipos, umbrales, usuarios y avisos.',
  tecnico: 'Opera y atiende alarmas de su cliente, sin modificar la configuración.',
  cliente_final:
    'Consulta el estado y las alarmas de su cliente. Puede accionar actuadores si se le concede en Permisos.',
};

export function FormularioUsuario({
  abierto,
  usuario,
  clientes,
  guardando,
  onCerrar,
  onGuardar,
}: FormularioUsuarioProps) {
  const [campos, setCampos] = useState(CAMPOS_VACIOS);
  const [error, setError] = useState<string | null>(null);

  /*
   * El correo no viene en el listado: `v2_list_users_by_scope` no lo devuelve
   * porque vive en `auth.users`, no en `perfiles`. Sí lo devuelve
   * `v2_get_user_detail`, así que se pide al abrir la ficha —igual que hacía la
   * aplicación anterior— en lugar de mostrar el campo vacío.
   */
  const detalle = useQuery({
    queryKey: ['usuario-detalle', usuario?.id],
    queryFn: () => repositorioUsuarios.detalle(usuario!.id),
    enabled: abierto && usuario !== null,
    staleTime: 30_000,
  });

  const correoGuardado = detalle.data?.usuario.email ?? usuario?.email ?? null;

  const esNuevo = usuario === null;

  useEffect(() => {
    if (!abierto) return;

    setError(null);
    setCampos(
      usuario
        ? {
            email: correoGuardado ?? '',
            clave: '',
            nombre: usuario.nombre,
            telefono: usuario.telefono ?? '',
            rol: usuario.rol.canonico,
            clienteId: String(usuario.clienteId ?? ''),
          }
        : CAMPOS_VACIOS,
    );
    // A propósito sin `correoGuardado`: reiniciar el formulario cada vez que
    // llega borraría lo que la persona esté escribiendo. Lo rellena el efecto
    // de abajo, que sólo toca ese campo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto, usuario]);

  // El correo llega después, cuando responde la ficha. Se coloca solo, sin
  // pisar nada más de lo que haya en pantalla.
  useEffect(() => {
    if (!correoGuardado) return;
    setCampos((c) => (c.email === correoGuardado ? c : { ...c, email: correoGuardado }));
  }, [correoGuardado]);

  // Un superadministrador no pertenece a ningún cliente: su alcance es global.
  const exigeCliente = campos.rol !== 'superadmin';

  const enviar = () => {
    setError(null);

    if (!campos.nombre.trim()) {
      setError('Indica el nombre de la persona.');
      return;
    }

    if (esNuevo) {
      const correo = campos.email.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(correo)) {
        setError('El correo no tiene un formato válido.');
        return;
      }
      if (campos.clave.length < LARGO_MINIMO_CLAVE) {
        setError(`La contraseña inicial debe tener al menos ${LARGO_MINIMO_CLAVE} caracteres.`);
        return;
      }
    }

    if (exigeCliente && !campos.clienteId) {
      setError('Elige a qué cliente pertenece este usuario.');
      return;
    }

    onGuardar({
      email: campos.email.trim().toLowerCase(),
      claveInicial: campos.clave,
      nombre: campos.nombre.trim(),
      telefono: campos.telefono.trim() || null,
      rol: campos.rol,
      clienteId: exigeCliente ? Number(campos.clienteId) : null,
    });
  };

  return (
    <Modal
      abierto={abierto}
      onCerrar={onCerrar}
      titulo={esNuevo ? 'Nuevo usuario' : `Editar ${usuario.nombre}`}
      descripcion={
        esNuevo
          ? 'Se creará la cuenta y se le pedirá cambiar la contraseña al entrar.'
          : 'El correo no se puede cambiar desde aquí.'
      }
      pie={
        <>
          <Button variante="secundario" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button onClick={enviar} cargando={guardando} icono="guardar">
            Guardar
          </Button>
        </>
      }
    >
      <div className={estilos.formulario}>
        {error && (
          <div className={estilos.error}>
            <Icon nombre="errorContorno" tamano={15} />
            {error}
          </div>
        )}

        <Input
          etiqueta="Nombre"
          requerido
          placeholder="Nombre y apellido"
          value={campos.nombre}
          onChange={(e) => setCampos((c) => ({ ...c, nombre: e.target.value }))}
        />

        <Input
          etiqueta="Correo"
          type="email"
          icono="correo"
          requerido={esNuevo}
          disabled={!esNuevo}
          placeholder="nombre@empresa.com"
          value={campos.email}
          onChange={(e) => setCampos((c) => ({ ...c, email: e.target.value }))}
          ayuda={
            esNuevo
              ? 'Será su usuario para entrar'
              : detalle.isPending
                ? 'Cargando…'
                : detalle.isError
                  ? 'No se pudo recuperar el correo.'
                  : undefined
          }
        />

        {esNuevo && (
          <Input
            etiqueta="Contraseña inicial"
            alternarVisibilidad
            requerido
            value={campos.clave}
            onChange={(e) => setCampos((c) => ({ ...c, clave: e.target.value }))}
            ayuda={`Mínimo ${LARGO_MINIMO_CLAVE} caracteres. Se le pedirá cambiarla al entrar por primera vez.`}
          />
        )}

        <Input
          etiqueta="Teléfono"
          icono="telefono"
          placeholder="Opcional"
          value={campos.telefono}
          onChange={(e) => setCampos((c) => ({ ...c, telefono: e.target.value }))}
        />

        <Select
          etiqueta="Rol"
          requerido
          value={campos.rol}
          onChange={(e) =>
            setCampos((c) => ({
              ...c,
              rol: e.target.value as RolCanonico,
              // Al pasar a superadministrador el cliente deja de aplicar.
              clienteId: e.target.value === 'superadmin' ? '' : c.clienteId,
            }))
          }
          ayuda={QUE_PUEDE_HACER[campos.rol]}
          opciones={ROLES_CANONICOS.map((r) => ({ valor: r, etiqueta: Rol.from(r).etiqueta }))}
        />

        {exigeCliente ? (
          <Select
            etiqueta="Cliente"
            requerido
            placeholder="Elige un cliente"
            value={campos.clienteId}
            onChange={(e) => setCampos((c) => ({ ...c, clienteId: e.target.value }))}
            ayuda="Sólo verá los datos de este cliente"
            opciones={clientes.map((c) => ({ valor: c.id, etiqueta: c.nombre }))}
          />
        ) : (
          <p className={estilos.ayuda}>
            Un superadministrador no pertenece a ningún cliente: ve todos.
          </p>
        )}
      </div>
    </Modal>
  );
}
